// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <https://benrichardson.dev>
// Based on QuakeWatch by Ben Richardson — https://benrichardson.dev
//
// Drawer + modal + glossary popover. The drawer/modal are DETACHED from the DOM
// when closed (not parked at translateX(100%)) — a closed off-canvas panel is a
// real box on iOS Safari and causes horizontal page scroll; removing it avoids
// that entirely.
import { esc } from '../format';
import { GLOSSARY } from '../glossary';

let drawerEl: HTMLElement | null = null;
let drawerScrim: HTMLElement | null = null;
let modalEl: HTMLElement | null = null;
let onDrawerClose: (() => void) | null = null;

/** Inline glossary link span. */
export function gterm(slug: string, text?: string): string {
  const g = GLOSSARY[slug];
  const label = text ?? g?.term ?? slug;
  return `<span class="gloss-link" data-term="${esc(slug)}" role="button" tabindex="0">${esc(label)}</span>`;
}

/** Small ℹ info icon that opens a glossary popover. */
export function infoI(slug: string): string {
  return `<i class="info-i" data-term="${esc(slug)}" role="button" tabindex="0" aria-label="What is this?">i</i>`;
}

export function openDrawer(title: string, bodyHtml: string, onClose?: () => void): void {
  closeDrawer();
  onDrawerClose = onClose ?? null;
  drawerScrim = document.createElement('div');
  drawerScrim.className = 'drawer-scrim';
  drawerScrim.addEventListener('click', () => closeDrawer());

  drawerEl = document.createElement('aside');
  drawerEl.className = 'drawer';
  drawerEl.setAttribute('role', 'dialog');
  drawerEl.setAttribute('aria-label', title);
  drawerEl.innerHTML = `
    <div class="drawer-head">
      <div class="drawer-title">${esc(title)}</div>
      <button class="icon-btn" data-close aria-label="Close">✕</button>
    </div>
    <div class="drawer-body">${bodyHtml}</div>`;
  drawerEl.querySelector('[data-close]')!.addEventListener('click', () => closeDrawer());

  document.body.appendChild(drawerScrim);
  document.body.appendChild(drawerEl);
  // Force reflow so the open transition runs (rAF is throttled in background tabs).
  void drawerEl.offsetWidth;
  drawerScrim.classList.add('open');
  drawerEl.classList.add('open');
}

export function closeDrawer(): void {
  const el = drawerEl;
  const scrim = drawerScrim;
  const cb = onDrawerClose;
  drawerEl = null;
  drawerScrim = null;
  onDrawerClose = null;
  if (el) {
    el.classList.remove('open');
    setTimeout(() => el.remove(), 260);
  }
  if (scrim) {
    scrim.classList.remove('open');
    setTimeout(() => scrim.remove(), 260);
  }
  if (cb) cb();
}

export function isDrawerOpen(): boolean {
  return drawerEl !== null;
}

export function openModal(title: string, bodyHtml: string): void {
  closeModal();
  const scrim = document.createElement('div');
  scrim.className = 'modal-scrim';
  scrim.innerHTML = `
    <div class="modal" role="dialog" aria-modal="true" aria-label="${esc(title)}">
      <div class="modal-head">
        <div class="drawer-title">${esc(title)}</div>
        <button class="icon-btn" data-close aria-label="Close">✕</button>
      </div>
      <div class="modal-body">${bodyHtml}</div>
    </div>`;
  scrim.addEventListener('click', (e) => { if (e.target === scrim) closeModal(); });
  scrim.querySelector('[data-close]')!.addEventListener('click', () => closeModal());
  modalEl = scrim;
  document.body.appendChild(scrim);
  void scrim.offsetWidth;
  scrim.classList.add('open');
}

export function closeModal(): void {
  const el = modalEl;
  modalEl = null;
  if (el) {
    el.classList.remove('open');
    setTimeout(() => el.remove(), 260);
  }
}

// ── Glossary popover ──
let glossPop: HTMLElement | null = null;

function closeGloss(): void {
  if (glossPop) { glossPop.remove(); glossPop = null; }
}

function showGloss(slug: string, anchor: Element): void {
  closeGloss();
  const g = GLOSSARY[slug];
  if (!g) return;
  const pop = document.createElement('div');
  pop.className = 'gloss-pop';
  pop.innerHTML = `<h5>${esc(g.term)}</h5><p>${esc(g.def)}</p>`;
  document.body.appendChild(pop);
  const r = anchor.getBoundingClientRect();
  const pr = pop.getBoundingClientRect();
  let left = r.left;
  let top = r.bottom + 8;
  if (left + pr.width + 12 > window.innerWidth) left = window.innerWidth - pr.width - 12;
  if (top + pr.height + 12 > window.innerHeight) top = r.top - pr.height - 8;
  pop.style.left = `${Math.max(8, left)}px`;
  pop.style.top = `${Math.max(8, top)}px`;
  glossPop = pop;
}

/** One-time global wiring: glossary popovers + Escape to close overlays. */
export function initOverlays(): void {
  document.addEventListener('click', (e) => {
    const term = (e.target as Element).closest('[data-term]');
    if (term) {
      e.stopPropagation();
      showGloss(term.getAttribute('data-term')!, term);
      return;
    }
    if (glossPop && !(e.target as Element).closest('.gloss-pop')) closeGloss();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      if (glossPop) { closeGloss(); return; }
      if (modalEl) { closeModal(); return; }
      if (drawerEl) { closeDrawer(); return; }
    }
    if ((e.key === 'Enter' || e.key === ' ') && (e.target as Element).matches?.('[data-term]')) {
      e.preventDefault();
      showGloss((e.target as Element).getAttribute('data-term')!, e.target as Element);
    }
  });
  window.addEventListener('resize', closeGloss);
  window.addEventListener('scroll', closeGloss, true);
}
